/**
 * AuthGuard Component
 * 
 * Protects routes that require authentication.
 * Shows loading state during session validation to prevent content flash.
 */

import { Loader2, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "../stores/auth";

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, apiKey, authMode, validateSession } = useAuthStore();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      // For session auth, apiKey is null — validate via cookie instead
      if (!apiKey && authMode !== 'session') {
        setIsValidating(false);
        setIsValid(false);
        return;
      }

      try {
        const valid = await validateSession();
        setIsValid(valid);
      } catch {
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    }

    checkAuth();
  }, [apiKey, authMode, validateSession]);

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-success-500 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Validating session...</span>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isValid || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render protected content
  return <>{children}</>;
}
